/** Per-token cards — one per TimestampToken, grouped by backend so an
 * auditor sees Tezos vs RFC 3161 anchor status side-by-side. Includes
 * deep-link to tzkt.io for anchored Tezos tokens. */
import { useTranslation } from "react-i18next";
import { formatDateTime } from "@/lib/format";

import type { TokenResult, TsaBackend } from "./types";


type Props = {
  tokens: TokenResult[];
};


export function TokenPanel({ tokens }: Props) {
  const { t } = useTranslation();
  return (
    <section className="card space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {t("ui.tokenPanel.heading")}
      </h2>
      {tokens.length === 0 ? (
        <p className="py-2 text-sm text-nb-text-3">
          {t("ui.tokenPanel.noAnchors")}
        </p>
      ) : (
        <ul className="space-y-2">
          {tokens.map((t) => (
            <TokenCard key={t.token_id} token={t} />
          ))}
        </ul>
      )}
    </section>
  );
}


function TokenCard({ token }: { token: TokenResult }) {
  const { t } = useTranslation();
  const stateBadge = stateBadgeFor(token);
  return (
    <li className="space-y-2 rounded-md border border-nb-border bg-slate-900/40 p-3">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-nb-text">
            {backendLabel(token.backend)}
          </span>
          <span className={`text-xs ${stateBadge.className}`}>
            {t(stateBadge.key)}
          </span>
        </div>
        <span className="text-xs text-nb-text-3">
          {t("ui.tokenPanel.covers", { seq: token.covers_sequence_through })}
        </span>
      </header>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
        <Pair
          label={t("ui.tokenPanel.head")}
          value={
            <code className="font-mono text-nb-text">
              {token.chain_head_hash.slice(0, 16)}…
            </code>
          }
        />
        {token.submitted_at && (
          <Pair
            label={t("ui.tokenPanel.submitted")}
            value={formatDateTime(token.submitted_at)}
          />
        )}
        {token.anchored_at && (
          <Pair
            label={t("ui.tokenPanel.anchored")}
            value={formatDateTime(token.anchored_at)}
          />
        )}
        {token.anchor_tx_hash && (
          <Pair
            label={t("ui.tokenPanel.tx")}
            value={
              <TzktTxLink
                tx={token.anchor_tx_hash}
                blockchain={token.blockchain}
              />
            }
          />
        )}
        {token.anchor_block_hash && (
          <Pair
            label={t("ui.tokenPanel.block")}
            value={
              <code className="font-mono text-nb-text">
                {token.anchor_block_hash.slice(0, 16)}…
              </code>
            }
          />
        )}
        {token.anchor_block_depth !== undefined && token.anchor_block_depth !== null && (
          <Pair
            label={t("ui.tokenPanel.depth")}
            value={t("ui.tokenPanel.depthValue", { count: token.anchor_block_depth })}
          />
        )}
        {token.anchor_serial && (
          <Pair
            label={t("ui.tokenPanel.serial")}
            value={<code className="font-mono text-nb-text">{token.anchor_serial}</code>}
          />
        )}
        {token.anchor_url && (
          <Pair
            label={t("ui.tokenPanel.tsa")}
            value={
              <a
                href={token.anchor_url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-nb-mint hover:underline"
              >
                {hostnameOf(token.anchor_url)} ↗
              </a>
            }
          />
        )}
      </dl>
      {token.details &&
        typeof (token.details as { error?: string }).error === "string" && (
          <p className="rounded bg-cvp-danger/10 px-2 py-1 text-xs text-cvp-danger">
            {(token.details as { error: string }).error}
          </p>
        )}
    </li>
  );
}


function TzktTxLink({ tx, blockchain }: { tx: string; blockchain?: string }) {
  const host = (blockchain ?? "tezos").toLowerCase() === "tezos" ? "tzkt.io" : null;
  if (!host) {
    return <code className="font-mono text-nb-text">{tx.slice(0, 16)}…</code>;
  }
  const url = `https://${host}/${encodeURIComponent(tx)}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className="font-mono text-nb-mint hover:underline"
    >
      {tx.slice(0, 16)}… ↗
    </a>
  );
}


function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}


function Pair({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-20 shrink-0 text-nb-text-3">{label}</dt>
      <dd className="text-nb-text">{value}</dd>
    </div>
  );
}


function backendLabel(backend: TsaBackend): string {
  switch (backend) {
    case "tezos_yodaledger":
      // YodaLedger is our relay; user-facing label leads with the blockchain.
      return "Tezos blockchain";
    case "rfc3161_freetsa":
      return "RFC 3161 (FreeTSA)";
    case "rfc3161_datasure":
      return "RFC 3161 (DataSure)";
    case "rfc3161_generic":
      return "RFC 3161";
    default:
      return backend;
  }
}


function stateBadgeFor(token: TokenResult): { key: string; className: string } {
  if (token.state === "failed") {
    return { key: "ui.tokenPanel.stateFailed", className: "text-cvp-danger" };
  }
  if (token.state === "pending") {
    return { key: "ui.tokenPanel.statePending", className: "text-cvp-warn" };
  }
  // anchored
  if (!token.ok) {
    return { key: "ui.tokenPanel.stateVerifyFailed", className: "text-cvp-danger" };
  }
  return { key: "ui.tokenPanel.stateAnchored", className: "text-cvp-ok" };
}
